import { useToast } from '@chakra-ui/react'
import { faArrowsRotate, faCircleInfo } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import FormButton from '../../../../components/FormButton'
import PageWrapper from '../../../../components/Pagewrapper'
import Toast from '../../../../components/Toast'
import Tooltip from '../../../../components/Tooltip'
import { VELI_CUTOVER_DATE_LABEL } from '../../../../constants'
import {
  coreApi,
  useAcceptClientAgreementMutation,
  useDeclareKycResidenceMutation,
  useGetComplianceOnboardingStateQuery,
  useGetUserQuery,
  useGetVeliKycStepStateQuery,
  useResendVerificationEmailMutation,
  useSubmitComplianceOnboardingAnswersMutation,
} from '../../../../redux/services/coreApi'
import type { RootState } from '../../../../redux/store'
import type { ComplianceOnboardingAnswersDto } from '../../../../types/coreApi-types'
import Cancelled from './components/Cancelled'
import ClientAgreementModal from './components/ClientAgreementModal'
import ComplianceRejected from './components/ComplianceRejected'
import Completed from './components/Completed'
import ComplianceQuestionnaireModal from './components/ComplianceQuestionnaireModal'
import KycResidenceModal from './components/KycResidenceModal'
import Step from './components/Step'

type GetStartedScreenProps = {}

const GetStartedScreen: React.FC<GetStartedScreenProps> = () => {
  const toast = useToast()
  const history = useHistory()
  const dispatch = useDispatch()

  const sessionUser = useSelector((state: RootState) => state.session.user)
  const [pollingInterval, setPollingInterval] = useState(2000) // Start with polling
  const { data: user } = useGetUserQuery(sessionUser.id, {
    pollingInterval,
  })

  const [isEmailVerified, setIsEmailVerified] = useState<boolean>(
    user?.emailVerified ?? false
  )

  // Keep the local verified flag in sync — it never regresses once true.
  useEffect(() => {
    if (user?.emailVerified) {
      setIsEmailVerified(true)
    }
  }, [user?.emailVerified])

  // Keep polling while we're waiting on a server-driven change the user can't
  // trigger from this screen: email verification landing, or a Sumsub webhook
  // advancing the KYC status (Pending → Processing → Approved/Declined). This is
  // what lets the screen update itself once Sumsub confirms — without it, a user
  // who finished KYC would sit on stale copy until they manually refreshed.
  // Approved/Declined are terminal (the screen swaps to Completed/Cancelled), so
  // we stop there rather than polling forever.
  const kycStatusValue = user?.kycDetails?.kycStatus
  useEffect(() => {
    const awaitingServerUpdate =
      !user?.emailVerified ||
      kycStatusValue === 'Pending' ||
      kycStatusValue === 'Processing' ||
      kycStatusValue === 'Completed'
    setPollingInterval(awaitingServerUpdate ? 2000 : 0)
  }, [user?.emailVerified, kycStatusValue])

  const [resendVerificationEmail, { isLoading: isResendingVerificationEmail }] =
    useResendVerificationEmailMutation()

  const [acceptClientAgreement, { isLoading: isAcceptingClientAgreement }] =
    useAcceptClientAgreementMutation()

  const [
    submitComplianceOnboardingAnswers,
    { isLoading: isSubmittingQuestionnaire },
  ] = useSubmitComplianceOnboardingAnswersMutation()

  const [declareKycResidence, { isLoading: isDeclaringResidence }] =
    useDeclareKycResidenceMutation()

  // The authoritative compliance verdict for this account. When a plausibility
  // review, EDD, or a high-risk score routes the case to a manual compliance
  // review (ComplianceStateMachine → ActivePendingMlro/Edd), the customer is
  // NOT "all set" even though every step they can do is done — Compliance still
  // has to clear the case, and may come back with questions. We can't infer
  // this from KYC status: a clean Sumsub result still flips KYC to Approved, so
  // a held case would otherwise fall straight through to the ready-to-go screen
  // and read as "everything is complete".
  const { data: complianceOnboardingState } =
    useGetComplianceOnboardingStateQuery()
  const isUnderComplianceReview =
    complianceOnboardingState?.state === 'ActivePendingMlro' ||
    complianceOnboardingState?.state === 'Edd'

  const [isAgreementModalOpen, setIsAgreementModalOpen] = useState(false)
  const [isQuestionnaireModalOpen, setIsQuestionnaireModalOpen] =
    useState(false)
  const [isResidenceModalOpen, setIsResidenceModalOpen] = useState(false)

  const isOrganization = user?.role === 'Organization'
  const kycStatus = user?.kycDetails
  const [hasSentMail, setHasResendMail] = useState(false)

  // Server-authoritative (ADR-0004): this user transferred in via the Veli
  // activation flow. It changes copy and the KYC step's state (whether an
  // earlier Veli reuse carried over), never the onboarding logic itself. Being
  // derived from the user, it survives logout/login.
  const veliOrigin = user?.isVeliOrigin ?? false
  const isKycPending = kycStatus?.kycStatus === 'Pending'

  // KYC is with Sumsub for review: the client has done their part (their input
  // is "done") and there is nothing more for them to continue — we're just
  // waiting on the result webhook. Drives a "verifying" state instead of a
  // "Continue" button that would only reopen the WebSDK to say "you're all set".
  const isKycProcessing = kycStatus?.kycStatus === 'Processing'

  // The KYC-step state is read on load (ADR-0004 §9), so the step can describe it
  // before the client acts: Reused if their Veli verification was carried over before
  // reusable KYC was retired, otherwise fresh KYC. Skipped for non-Veli users and once
  // KYC is past Pending, so nobody else makes the call. The screen branches its copy off
  // this response — never on a User-cache refetch landing in time, so there is no render race.
  const isVeliKycStep = veliOrigin && isKycPending
  const { data: veliStepState, isFetching: isCheckingReuse } =
    useGetVeliKycStepStateQuery(undefined, { skip: !isVeliKycStep })
  const reuseOutcome = veliStepState?.outcome
  const kycLabel = isOrganization ? 'KYB' : 'KYC'

  // Compliance risk-assessment questionnaire (Q0–Q10, CRM-001 v7.8)
  const riskAssessmentQuestionnaire = user?.requiredQuestionnaires?.find(
    q => q.type === 'RiskAssessment'
  )
  const needsRiskAssessment =
    !riskAssessmentQuestionnaire ||
    riskAssessmentQuestionnaire?.status === 'Requested' ||
    riskAssessmentQuestionnaire?.status === 'NotRequested'
  const hasCompletedRiskAssessment =
    riskAssessmentQuestionnaire?.status === 'Completed'

  // Every step the client can do is done. Whether the account is live now
  // depends on the compliance verdict, which the client can't see: most
  // accounts clear automatically and fall through to <Completed/>, but some are
  // held for a manual compliance review.
  const hasCompletedEveryStep =
    (kycStatus?.kycStatus === 'Completed' ||
      kycStatus?.kycStatus === 'Approved') &&
    isEmailVerified &&
    !!user?.hasAcceptedClientAgreement &&
    (!needsRiskAssessment || hasCompletedRiskAssessment)

  // The account is held for a manual compliance review. Two ways to land here:
  // the compliance engine routed the case to review (plausibility review / EDD
  // / high risk → isUnderComplianceReview), or KYC passed its checks but hasn't
  // been Approved yet (kycStatus 'Completed'). Either way the client is done
  // and just waiting — we tell them so rather than let a KYC-Approved held case
  // read as "you're all set".
  const isAwaitingManualReview =
    hasCompletedEveryStep &&
    (isUnderComplianceReview || kycStatus?.kycStatus === 'Completed')

  // Progress for the Veli interim header — momentum is the antidote to the
  // "is this broken?" feeling. Mirrors the per-step status logic below so the
  // count never disagrees with the checklist: account is always done, the rest
  // flip as the client completes them. The risk assessment only counts toward
  // the total when it's actually required for this account.
  const isKycDone =
    kycStatus?.kycStatus === 'Completed' || kycStatus?.kycStatus === 'Approved'
  const totalSteps = needsRiskAssessment ? 5 : 4
  const completedSteps =
    1 +
    (isEmailVerified ? 1 : 0) +
    (isKycDone ? 1 : 0) +
    (user?.hasAcceptedClientAgreement ? 1 : 0) +
    (needsRiskAssessment && hasCompletedRiskAssessment ? 1 : 0)

  // Refused on compliance grounds (e.g. the pre-KYC country gate blanket-rejecting an unsupported country of
  // residence). Terminal — show the rejection copy rather than the onboarding checklist.
  if (
    complianceOnboardingState?.state === 'Rejected' ||
    complianceOnboardingState?.state === 'Exited'
  ) {
    return <ComplianceRejected />
  }

  if (kycStatus?.kycStatus === 'Declined') {
    return <Cancelled />
  }

  // Fully onboarded AND cleared by compliance → the ready-to-go screen. A case
  // held for a manual compliance review (plausibility review / EDD / high risk)
  // is NOT cleared, so we skip this and fall through to the review status below
  // rather than telling the customer their account is ready. (Veli clients keep
  // their holding screen either way — their interim state is about the
  // portfolio move, not the compliance verdict.)
  if (
    kycStatus?.kycStatus === 'Approved' &&
    user?.hasAcceptedClientAgreement &&
    (!needsRiskAssessment || hasCompletedRiskAssessment) &&
    (veliOrigin || !isUnderComplianceReview)
  ) {
    return <Completed />
  }

  // KYC step entry. The step state was already read on load (getVeliKycStepState), so this
  // is just navigation: the WebSDK opens on the right Sumsub level for this client (the
  // server picks the Veli level for a Reused client so they continue on the imported
  // documents and fill only the gaps, the level their declared residence routes to otherwise —
  // ADR-0004 §9 / ADR-0015).
  const handleStartKyc = () => {
    // Organizations (KYB) are not residence-routed or gated, and a Veli client whose Veli verification was
    // reused continues on the Veli level where the imported documents live — straight to verification for
    // both. Every other individual, direct or Veli-origin verifying fresh, declares their country of residence
    // first (the pre-KYC country gate, ADR-0015 — it soft-blocks an unsupported country and picks Non-Doc vs
    // the document flow), unless one is already committed: the gate locks it (the Sumsub applicant is created
    // at the level it implies and can't be re-routed), so we continue the existing verification rather than
    // re-ask.
    if (
      isOrganization ||
      reuseOutcome === 'Reused' ||
      user?.declaredResidenceIso
    ) {
      history.push('/app/kyc/sumsub')
      return
    }
    setIsResidenceModalOpen(true)
  }

  // Returns the gate outcome so the modal can react: 'eligible' → we navigate to KYC; 'blocked' → the modal
  // shows a recoverable "we can't serve your country" message (no freeze — the declaration is unverified);
  // 'error' → we toast and let them retry.
  const handleSubmitResidence = async (
    residenceIso: string
  ): Promise<'eligible' | 'blocked' | 'error'> => {
    try {
      const { eligible } = await declareKycResidence({ residenceIso }).unwrap()
      if (!eligible) return 'blocked'
      setIsResidenceModalOpen(false)
      // Pull the committed declaredResidenceIso into the User cache so a later Start skips the modal.
      dispatch(coreApi.util.invalidateTags(['User']))
      history.push('/app/kyc/sumsub')
      return 'eligible'
    } catch (error) {
      toast({
        position: 'top-right',
        render: () => (
          <Toast type="error">
            We couldn't check your country of residence. Please try again.
          </Toast>
        ),
      })
      return 'error'
    }
  }

  const handleAcceptAgreement = async () => {
    try {
      await acceptClientAgreement(null).unwrap()
      setIsAgreementModalOpen(false)
      toast({
        position: 'top-right',
        render: () => {
          return (
            <Toast type="success">
              Client agreement accepted successfully.
            </Toast>
          )
        },
      })
      dispatch(coreApi.util.invalidateTags(['User']))
    } catch (error) {
      toast({
        position: 'top-right',
        render: () => {
          return (
            <Toast type="error">
              Failed to accept client agreement. Please try again.
            </Toast>
          )
        },
      })
    }
  }

  const handleSubmitQuestionnaire = async (
    data: ComplianceOnboardingAnswersDto
  ) => {
    try {
      await submitComplianceOnboardingAnswers(data).unwrap()
      setIsQuestionnaireModalOpen(false)
      toast({
        position: 'top-right',
        render: () => {
          return (
            <Toast type="success">
              Risk assessment submitted successfully.
            </Toast>
          )
        },
      })
      dispatch(coreApi.util.invalidateTags(['User']))
    } catch (error) {
      toast({
        position: 'top-right',
        render: () => {
          return (
            <Toast type="error">
              Failed to submit risk assessment. Please try again.
            </Toast>
          )
        },
      })
    }
  }

  return (
    <PageWrapper fixedWidth embedded={veliOrigin}>
      <div
        className={`card gap-2 md:gap-4 relative ${
          veliOrigin ? 'mt-4 rounded-2xl shadow-sm md:p-8' : 'mt-20'
        }`}
      >
        <button
          id="testBtn"
          onClick={async () => {
            const testBtn = document.getElementById('testBtn')
            testBtn?.focus()
            setTimeout(() => {
              testBtn?.blur()
            }, 600)

            if (!isEmailVerified) {
              //@ts-ignore
              window.location = window.location
            } else {
              dispatch(
                coreApi.util.invalidateTags(['User', 'Users', 'Members'])
              )
            }
          }}
          className="transition-all focus:rotate-[360deg] duration-500 ease-out absolute right-7 top-7"
        >
          <Tooltip label={`Update`} closeOnClick>
            <FontAwesomeIcon icon={faArrowsRotate} />
          </Tooltip>
        </button>
        <div className="text-center mt-5">
          {veliOrigin ? (
            <>
              {/* Clear the refresh button (absolute, top-right) on mobile, where
                  the centred title would otherwise run under it. */}
              <h1 className="font-semi text-2xl md:text-3xl px-10 md:px-0">
                Set up your Penning account
              </h1>
              <p className="text-description mt-2 max-w-md mx-auto">
                Finish these steps so you're ready when we move your account.
              </p>
            </>
          ) : (
            <>
              <h1 className="font-semi text-lg">Welcome to Penning! </h1>
              <p className="text-description">
                Here's everything we need to finish setting up your account.
                Please work through every step below — not just identity
                verification, but the client agreement and questionnaire too.
                Completing all of them is what activates your account and keeps
                it active.
              </p>
            </>
          )}
        </div>

        {/* Momentum for the interim: one progress bar, one count. Shown only to
            Veli clients — the standard onboarding screen is left unchanged. */}
        {veliOrigin && (
          <div className="flex items-center gap-3 mt-8">
            <div className="flex-1 h-2 rounded-full bg-black/10 overflow-hidden">
              <div
                className="h-full bg-[#2E9E6B] rounded-full transition-all duration-500"
                style={{
                  width: `${(completedSteps / totalSteps) * 100}%`,
                }}
              />
            </div>
            <span className="text-sm text-description shrink-0">
              {completedSteps} of {totalSteps} done
            </span>
          </div>
        )}

        <div className="space-y-5 mt-10">
          <Step
            status="completed"
            step={1}
            title="Create your account"
            description="Take your first step towards becoming a Penning investor."
          />
          <Step
            status={!isEmailVerified ? 'in-progress' : 'completed'}
            step={2}
            title="Verify your email"
            description="We have sent you an email. This helps us know that you own this account."
          >
            <FormButton
              disabled={hasSentMail}
              isLoading={isResendingVerificationEmail}
              loadingText="Sending.."
              title="Resend email"
              onClick={async () => {
                await resendVerificationEmail(null).unwrap()
                setHasResendMail(true)
                toast({
                  position: 'top-right',
                  render: () => {
                    return (
                      <Toast type="success">
                        We have sent you a new link to {sessionUser?.email}{' '}
                        verify your email.
                      </Toast>
                    )
                  },
                })
              }}
            />
          </Step>

          <Step
            step={3}
            status={
              (kycStatus?.kycStatus === 'Pending' ||
                kycStatus?.kycStatus === 'Processing') &&
              isEmailVerified
                ? 'in-progress'
                : kycStatus?.kycStatus === 'Completed' ||
                    kycStatus?.kycStatus === 'Approved'
                  ? 'completed'
                  : 'pending'
            }
            title={
              isVeliKycStep
                ? isCheckingReuse
                  ? `Checking your Veli ${kycLabel}`
                  : reuseOutcome === 'Reused'
                    ? `Continue your ${kycLabel} process`
                    : `Start your ${kycLabel} process`
                : isKycProcessing
                  ? `Verifying your ${kycLabel}`
                  : `${isKycPending ? 'Start' : 'Continue'} your ${kycLabel} process`
            }
            description={
              isVeliKycStep
                ? isCheckingReuse
                  ? `We're checking whether we can carry over the ${
                      isOrganization ? 'company ' : ''
                    }verification you already completed with Veli.`
                  : reuseOutcome === 'Reused'
                    ? isOrganization
                      ? "Good news — we carried over the company verification documents you already gave Veli. Complete the few remaining steps and you're done."
                      : "Good news — we carried over the identity documents you already uploaded to Veli. Complete the few remaining steps and you're done."
                    : isOrganization
                      ? "We couldn't carry over your existing Veli verification, so we'll need to verify your company's identity before you can use Penning."
                      : "We couldn't carry over your existing Veli verification, so we'll need a quick identity check before you can use Penning."
                : isKycProcessing
                  ? isOrganization
                    ? "Thanks — we've received your details and we're confirming your company's verification. There's nothing more for you to do; this page updates automatically once it's done."
                    : "Thanks — we've received your details and we're confirming your identity. There's nothing more for you to do; this page updates automatically once it's done."
                  : isOrganization
                    ? "We need to verify your company's identity and ownership structure before you can use Penning."
                    : 'We need to identify and verify your identity (using your passport) before you can use Penning.'
            }
          >
            {/* While processing, the client's part is done — so instead of a
                "Continue" button that would reopen the WebSDK only to report
                "you're all set", we show a passive waiting indicator. */}
            {isKycProcessing ? (
              <div className="flex items-center gap-2 text-sm text-description">
                <FontAwesomeIcon
                  icon={faArrowsRotate}
                  className="animate-spin"
                />
                <span>Verification in progress</span>
              </div>
            ) : (
              <FormButton
                title={
                  isVeliKycStep && reuseOutcome === 'Reused'
                    ? 'Continue verification'
                    : isKycPending
                      ? 'Start'
                      : 'Continue'
                }
                isLoading={isCheckingReuse}
                loadingText="Checking your Veli verification"
                onClick={handleStartKyc}
              />
            )}
          </Step>

          <Step
            step={4}
            status={
              (kycStatus?.kycStatus === 'Completed' ||
                kycStatus?.kycStatus === 'Approved') &&
              isEmailVerified
                ? user?.hasAcceptedClientAgreement
                  ? 'completed'
                  : 'in-progress'
                : 'pending'
            }
            title="Accept Client Agreement"
            description="Review and accept our client agreement before proceeding."
          >
            <FormButton
              title="View Agreement"
              isLoading={isAcceptingClientAgreement}
              onClick={() => setIsAgreementModalOpen(true)}
            />
          </Step>

          {needsRiskAssessment && (
            <Step
              step={5}
              status={
                (kycStatus?.kycStatus === 'Completed' ||
                  kycStatus?.kycStatus === 'Approved') &&
                isEmailVerified &&
                user?.hasAcceptedClientAgreement
                  ? hasCompletedRiskAssessment
                    ? 'completed'
                    : 'in-progress'
                  : 'pending'
              }
              title="Complete Risk Assessment"
              description="A short questionnaire about how you intend to use Penning, your source of funds, and your background. Required for regulatory compliance."
            >
              <FormButton
                title="Start Questionnaire"
                isLoading={isSubmittingQuestionnaire}
                onClick={() => setIsQuestionnaireModalOpen(true)}
              />
            </Step>
          )}

          {/* Manual review is not part of every onboarding — most accounts are
              approved automatically. So it's not shown as a greyed-out future
              step; it only appears, as a status, once an account is actually
              held for a manual compliance review. */}
          {isAwaitingManualReview && (
            <div className="rounded-md bg-bg-component p-4 text-sm text-description">
              <span className="font-semi">
                Your application has been submitted for review.
              </span>{' '}
              Our Compliance team is reviewing your application and will contact
              you if any further information is required. This isn't needed for
              every account, so there's nothing more for you to do right now —
              we'll email you once your account has been approved.
            </div>
          )}
        </div>

        {/* Permanent reassurance, anchored on the cutover date — the actual
            antidote to "wait, which platform do I use?". Present on every Veli
            interim screen. */}
        {veliOrigin && (
          <div className="mt-2 rounded-md bg-bg-component p-4 text-sm text-description flex items-start gap-3">
            <FontAwesomeIcon icon={faCircleInfo} className="mt-0.5 shrink-0" />
            <p>
              Until <span className="font-semi">{VELI_CUTOVER_DATE_LABEL}</span>
              , keep using Veli as normal. Your money stays exactly where it is
              — we'll move it for you.
            </p>
          </div>
        )}

        <div
          className={`h-[1px] bg-black/5 -mx-6 ${veliOrigin ? 'md:-mx-8' : ''}`}
        />
        <div className="flex flex-col items-center p-5 text-center">
          {veliOrigin ? (
            // A single quiet line for the interim landing — matching the calmer,
            // less form-like tone of the rest of the Veli frame.
            <span className="text-description">
              Questions? Write{' '}
              <a
                href="mailto:support@penning.io"
                className="hover:underline text-brand-blue"
              >
                support@penning.io
              </a>
            </span>
          ) : (
            <>
              <p className="font-medium mb-2">Experiencing any problems?</p>
              <span className="text-description">
                Write us on{' '}
                <a
                  href="mailto:support@penning.io"
                  className="hover:underline text-brand-blue"
                >
                  support@penning.io
                </a>
                .
              </span>
            </>
          )}
        </div>
      </div>

      <KycResidenceModal
        isOpen={isResidenceModalOpen}
        onClose={() => setIsResidenceModalOpen(false)}
        onSubmit={handleSubmitResidence}
        isLoading={isDeclaringResidence}
      />

      <ClientAgreementModal
        isOpen={isAgreementModalOpen}
        onClose={() => setIsAgreementModalOpen(false)}
        onAccept={handleAcceptAgreement}
        isLoading={isAcceptingClientAgreement}
      />

      <ComplianceQuestionnaireModal
        isOpen={isQuestionnaireModalOpen}
        onClose={() => setIsQuestionnaireModalOpen(false)}
        onSubmit={handleSubmitQuestionnaire}
        isLoading={isSubmittingQuestionnaire}
        // Migrated Veli clients don't choose a Penning product — Q0 is hidden
        // and they submit with no product. The risk model still adds the flat
        // Veli-migration score server-side, derived from the Veli-origin fact
        // (ADR-0008). Hiding Q0 avoids confusing them with a product choice.
        hideProductSelection={veliOrigin}
      />
    </PageWrapper>
  )
}

export default GetStartedScreen
